/**
 * Map mode preference — localStorage-persisted flat/globe choice.
 * Mirrors WM's map-mode-preference service: the persisted value is the
 * user-facing 'flat' | 'globe' pair, translated to MapContainer's
 * internal mode ids at the boundary.
 */

import type { MapMode } from './map-container';

export type MapModePreference = 'flat' | 'globe';

const STORAGE_KEY = 'sentinel-map-mode';
const DEFAULT_PREFERENCE: MapModePreference = 'flat';

/** Coerce any stored/foreign value to a valid preference. */
export function normalizeMapModePreference(value: string | null | undefined): MapModePreference {
  if (value === 'flat' || value === 'globe') return value;
  return DEFAULT_PREFERENCE;
}

/** Read the persisted preference (default 'flat'). */
export function getStoredMapModePreference(): MapModePreference {
  try {
    return normalizeMapModePreference(localStorage.getItem(STORAGE_KEY));
  } catch {
    return DEFAULT_PREFERENCE;
  }
}

/** Persist the preference. Quota/access errors are non-fatal. */
export function persistMapModePreference(preference: MapModePreference): void {
  try {
    localStorage.setItem(STORAGE_KEY, preference);
  } catch {
    // localStorage unavailable — preference lives for this session only
  }
}

/** Translate a preference to MapContainer's mode id. */
export function preferenceToMapMode(preference: MapModePreference): MapMode {
  return preference === 'globe' ? 'globe3d' : 'deckgl';
}

/** Translate a MapContainer mode id back to a preference. */
export function mapModeToPreference(mode: MapMode): MapModePreference {
  return mode === 'globe3d' ? 'globe' : 'flat';
}
